import cx from "classnames";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTimeout } from "react-use";
import { c, t } from "ttag";

import EmptyCodeResult from "assets/img/empty-states/code.svg";
import { LoadingSpinner } from "metabase/common/components/LoadingSpinner";
import { Warnings } from "metabase/common/components/Warnings";
import QueryBuilderS from "metabase/css/query_builder.module.css";
import { useSelector } from "metabase/redux";
import { getWhiteLabeledLoadingMessageFactory } from "metabase/selectors/whitelabel";
import { Box, Flex, Stack, Text, Title } from "metabase/ui";
import { isMac } from "metabase/utils/browser";
import { SERVER_ERROR_TYPES } from "metabase/utils/errors";
import * as Lib from "metabase-lib";
import { HARD_ROW_LIMIT } from "metabase-lib/v1/queries/utils";

import S from "./QueryVisualization.module.css";
import { RunButtonWithTooltip } from "./RunButtonWithTooltip";
import { VisualizationError } from "./VisualizationError";
import { VisualizationResult } from "./VisualizationResult";
import type { QueryVisualizationProps } from "./types";

const SLOW_MESSAGE_TIMEOUT = 4000;

export function QueryVisualization(props: QueryVisualizationProps) {
  const {
    className,
    question,
    isRunnable,
    isRunning,
    isObjectDetail,
    isResultDirty,
    isNativeEditorOpen,
    isDirtyStateShownForError,
    result,
    maxTableRows = HARD_ROW_LIMIT,
  } = props;

  const canRun = Lib.canRun(question.query(), question.type());
  const [warnings, setWarnings] = useState<string[]>([]);
  const isDirtyStateShown =
    canRun &&
    isResultDirty &&
    isRunnable &&
    !isRunning &&
    !isNativeEditorOpen &&
    (result?.error == null ||
      isDirtyStateShownForError ||
      result?.error_type === SERVER_ERROR_TYPES.missingRequiredParameter);

  return (
    <Box pos="relative" h="100%" className={cx(className, S.root)}>
      {isRunning ? <VisualizationRunningState className={S.overlay} /> : null}
      <VisualizationDirtyState
        {...props}
        hidden={!isDirtyStateShown}
        className={S.overlay}
      />
      {!isObjectDetail && (
        <Warnings warnings={warnings} className={S.warnings} size={18} />
      )}
      <Box
        className={cx(S.visualization, QueryBuilderS.Visualization, {
          [QueryBuilderS.VisualizationLoading]: isRunning,
        })}
        data-testid="query-visualization-root"
      >
        {result?.error ? (
          <VisualizationError
            className={S.spread}
            error={result.error}
            errorType={result.error_type}
            via={result.via ?? []}
            question={question}
            duration={result.duration ?? 0}
          />
        ) : result?.data ? (
          <VisualizationResult
            {...props}
            maxTableRows={maxTableRows}
            className={S.spread}
            onUpdateWarnings={setWarnings}
          />
        ) : !isRunning && !isDirtyStateShown ? (
          <VisualizationEmptyState>
            {t`Here's where your results will appear`}
          </VisualizationEmptyState>
        ) : null}
      </Box>
    </Box>
  );
}

const VisualizationEmptyState = ({ children }: { children: ReactNode }) => {
  const keyboardShortcut = getRunQueryShortcut();

  return (
    <Flex w="100%" h="100%" align="center" justify="center">
      <Stack maw="25rem" gap={0} ta="center" align="center">
        <Box maw="3rem" mb="0.75rem">
          <img src={EmptyCodeResult} alt="Code prompt icon" />
        </Box>
        <Text c="text-secondary">
          {c("{0} refers to the keyboard shortcut")
            .jt`To run your code, click on the Run button or type ${(
            <b key="shortcut">({keyboardShortcut})</b>
          )}`}
        </Text>
        <Text c="text-secondary">{children}</Text>
      </Stack>
    </Flex>
  );
};

// See the matching hook in LoadingAndErrorWrapper.tsx for why this defers
// via a plain effect (not requestAnimationFrame), and why the role/aria-live
// attributes below go on the Flex wrapping the already-visible Title rather
// than a separate hidden node: duplicating the message into a hidden
// announcer would make text queries against this component ambiguous.
function useDeferredText(text: string): string {
  const [deferred, setDeferred] = useState("");

  useEffect(() => {
    setDeferred(text);
  }, [text]);

  return deferred;
}

export function VisualizationRunningState({
  className = "",
}: {
  className?: string;
}) {
  const [isSlow] = useTimeout(SLOW_MESSAGE_TIMEOUT);

  const getLoadingMessage = useSelector(getWhiteLabeledLoadingMessageFactory);

  // show the slower loading message only when the loadingMessage is
  // not customized
  const message = getLoadingMessage(isSlow() ?? false);
  const deferredMessage = useDeferredText(message);

  return (
    <Flex
      className={cx(className, QueryBuilderS.Overlay)}
      c="core-brand"
      direction="column"
      justify="center"
      align="center"
      role="status"
      aria-live="polite"
    >
      <LoadingSpinner />
      <Title c="text-brand-strong" order={3} mt="lg">
        {deferredMessage}
      </Title>
    </Flex>
  );
}

type VisualizationDirtyStateProps = {
  className?: string;
  isRunning?: boolean;
  isResultDirty?: boolean;
  runQuestionQuery?: () => void;
  cancelQuery?: () => void;
  hidden?: boolean;
};

export const VisualizationDirtyState = ({
  className,
  isRunning,
  isResultDirty,
  runQuestionQuery,
  cancelQuery,
  hidden,
}: VisualizationDirtyStateProps) => {
  const keyboardShortcut = getRunQueryShortcut();

  const handleClick = () => {
    if (!hidden) {
      if (isRunning) {
        cancelQuery?.();
      } else {
        runQuestionQuery?.();
      }
    }
  };

  return (
    <Flex
      className={cx(className, QueryBuilderS.Overlay, {
        [QueryBuilderS.OverlayActive]: !hidden,
        [QueryBuilderS.OverlayHidden]: hidden,
      })}
      direction="column"
      justify="center"
      align="center"
      gap="sm"
      data-testid="run-button-overlay"
      onClick={handleClick}
    >
      <RunButtonWithTooltip
        className={S.shadowed}
        iconSize={32}
        circular
        hidden={hidden}
        isRunning={Boolean(isRunning)}
        isDirty={Boolean(isResultDirty)}
      />
      {!hidden && <Text c="text-secondary">{keyboardShortcut}</Text>}
    </Flex>
  );
};

function getRunQueryShortcut() {
  return isMac() ? t`⌘ + return` : t`Ctrl + enter`;
}
